import { API_URL, getAuthHeaders } from "@config/api";

/**
 * Upload S3 multipart resumável direto do navegador.
 *
 * Fluxo:
 *  1. POST create           -> { uploadId, key }
 *  2. POST part-url (por parte) -> { url } (presignada PUT no S3)
 *  3. PUT direto no S3 (XHR, com progresso) -> captura ETag
 *  4. POST complete         -> { path, ... }
 *  5. POST abort (em falha)  -> best-effort
 *
 * Cada parte tem 8 MiB, com retry (3 tentativas, backoff) e no máximo 3 partes
 * em paralelo. O progresso é agregado somando os bytes de todas as partes.
 */

const PART = 8 * 1024 * 1024; // 8 MiB
const MAX_CONCURRENCY = 3;
const MAX_RETRIES = 3;
const RETRY_BACKOFF_MS = [500, 1500, 3500];

interface MultipartIds {
  phaseFormId: string;
  processId: string;
  formFieldId: string;
}

interface CreateResponse {
  uploadId: string;
  key: string;
}

interface PartUrlResponse {
  url: string;
}

interface CompletedPart {
  PartNumber: number;
  ETag: string;
}

interface CompleteResponse {
  path: string;
  [key: string]: unknown;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Executa uma chamada JSON para o backend multipart.
 * Usa fetch com os headers de auth + Content-Type: application/json.
 * Em status !ok, lança Error com a mensagem do backend quando disponível.
 */
async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = `Erro do servidor (HTTP ${response.status})`;
    try {
      const data = await response.json();
      if (data && (data.message || data.error)) {
        message = data.message || data.error;
      }
    } catch {
      // corpo não-JSON ou vazio: mantém a mensagem padrão
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

/**
 * Erro que representa falha de conexão (rede) durante o PUT no S3,
 * distinto de erro de servidor/HTTP.
 */
class ConnectionError extends Error {
  constructor(message = "Falha de conexão durante o upload") {
    super(message);
    this.name = "ConnectionError";
  }
}

/**
 * Faz o PUT de uma parte diretamente no S3 via XMLHttpRequest (para ter
 * progresso). Retorna o ETag da parte. Reporta progresso incremental via
 * onChunkProgress(loadedDaParteAtual).
 */
function putPart(
  url: string,
  blob: Blob,
  onChunkProgress: (loaded: number) => void
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onChunkProgress(event.loaded);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag");
        if (!etag) {
          reject(
            new Error(
              "ETag não retornado pelo S3. Verifique a configuração de CORS do bucket (ExposeHeaders: ETag)."
            )
          );
          return;
        }
        resolve(etag);
      } else {
        reject(new Error(`Erro do servidor S3 (HTTP ${xhr.status})`));
      }
    };

    xhr.onerror = () => reject(new ConnectionError());
    xhr.ontimeout = () => reject(new ConnectionError("Tempo limite de conexão excedido"));
    xhr.onabort = () => reject(new ConnectionError("Upload da parte cancelado"));

    xhr.open("PUT", url, true);
    // 10 minutos por parte — resiliente a conexões lentas.
    xhr.timeout = 600000;
    xhr.send(blob);
  });
}

export async function uploadFileMultipart(
  file: File,
  ids: MultipartIds,
  onProgress?: (progress: number) => void
): Promise<{ path: string }> {
  const { phaseFormId, processId, formFieldId } = ids;
  const base = `/filledforms/${phaseFormId}/${processId}/${formFieldId}/multipart`;

  // 1. create
  const { uploadId, key } = await postJson<CreateResponse>(`${base}/create`, {
    fileName: file.name,
    fileType: file.type,
  });

  // Divide o arquivo em partes de 8 MiB. PartNumber começa em 1.
  const totalParts = Math.max(1, Math.ceil(file.size / PART));
  const partNumbers: number[] = [];
  for (let i = 0; i < totalParts; i++) partNumbers.push(i + 1);

  // Progresso agregado: acumulador de bytes concluídos + loaded da parte em voo.
  let completedBytes = 0;
  const inFlightLoaded = new Map<number, number>();

  const reportProgress = () => {
    if (!onProgress || file.size === 0) return;
    let live = 0;
    inFlightLoaded.forEach((v) => (live += v));
    const totalLoaded = Math.min(completedBytes + live, file.size);
    onProgress(Math.round((totalLoaded * 100) / file.size));
  };

  const completedParts: CompletedPart[] = [];

  /**
   * Envia uma parte com retry + backoff. Ao concluir, contabiliza os bytes
   * no acumulador de progresso.
   */
  const uploadPart = async (partNumber: number): Promise<void> => {
    const start = (partNumber - 1) * PART;
    const end = Math.min(start + PART, file.size);
    const blob = file.slice(start, end);
    const partSize = end - start;

    let lastError: unknown;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        // Presigned URL é pedida a cada tentativa (evita URL expirada).
        const { url } = await postJson<PartUrlResponse>(`${base}/part-url`, {
          key,
          uploadId,
          partNumber,
        });

        inFlightLoaded.set(partNumber, 0);
        const etag = await putPart(url, blob, (loaded) => {
          inFlightLoaded.set(partNumber, loaded);
          reportProgress();
        });

        // Sucesso: consolida os bytes desta parte.
        inFlightLoaded.delete(partNumber);
        completedBytes += partSize;
        reportProgress();

        completedParts.push({ PartNumber: partNumber, ETag: etag });
        return;
      } catch (error) {
        lastError = error;
        inFlightLoaded.delete(partNumber);
        reportProgress();

        if (attempt < MAX_RETRIES - 1) {
          await sleep(RETRY_BACKOFF_MS[attempt] ?? 3500);
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error("Falha ao enviar parte do arquivo");
  };

  // Pool simples de concorrência (máx 3 partes em paralelo).
  try {
    const queue = [...partNumbers];
    const runWorker = async (): Promise<void> => {
      while (queue.length > 0) {
        const partNumber = queue.shift();
        if (partNumber === undefined) return;
        await uploadPart(partNumber);
      }
    };

    const workers: Promise<void>[] = [];
    const workerCount = Math.min(MAX_CONCURRENCY, partNumbers.length);
    for (let i = 0; i < workerCount; i++) workers.push(runWorker());
    await Promise.all(workers);
  } catch (error) {
    // Falha irrecuperável: aborta (best-effort) e propaga erro claro.
    try {
      await postJson(`${base}/abort`, { key, uploadId });
    } catch {
      // ignora erro do abort
    }

    if (error instanceof ConnectionError) {
      throw new Error(
        "Falha de conexão durante o upload. Verifique sua internet e tente novamente."
      );
    }
    throw error instanceof Error
      ? new Error(`Falha no servidor durante o upload: ${error.message}`)
      : new Error("Falha no upload do arquivo");
  }

  // 4. complete — partes ordenadas por PartNumber.
  const parts = completedParts.sort((a, b) => a.PartNumber - b.PartNumber);
  const resp = await postJson<CompleteResponse>(`${base}/complete`, {
    key,
    uploadId,
    parts,
    fileName: file.name,
    fileType: file.type,
  });

  return { path: resp.path };
}
